import { AgreementStatus, AuthUserPermission, FieldModel } from '@cibo/core'
import {
  fieldAcresColumn,
  fieldNameLocationColumn,
  fieldThumbnailColumn,
  useFields,
} from '@cibo/landmanager'
import { Can, Loading, useUser } from '@cibo/profile'
import { DataGridPro, FriendlyError, Markdown, ResponsiveDialog } from '@cibo/ui'
import HandshakeRounded from '@mui/icons-material/HandshakeRounded'
import ThumbDownAltRounded from '@mui/icons-material/ThumbDownAltRounded'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { CONTROL_GROUPS_QUERY_KEY } from 'features/ControlGroups'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Forbidden } from '../../../pages/Forbidden'
import { AgreementNotes } from '../AgreementNotes'
import { AgreementResetButton } from '../AgreementResetButton'
import { useAgreement, useAgreementCopy, useUpdateAgreement } from '../queries'
import { AgreementDetailProps } from '../types'

export const ErrorReportDetail = ({
  agreementId,
  setActionModal,
  downloadButton,
  markdownOverrides,
}: AgreementDetailProps) => {
  const queryClient = useQueryClient()
  const { t } = useTranslation('impact/pages/Agreements/ErrorReportDetail')
  const agreement = useAgreement(agreementId)
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const user = useUser()
  const agreementCopy = useAgreementCopy(agreementId)
  const fields = useFields(agreement.data?.resourceIds ? agreement.data?.resourceIds : [])
  const { mutateAsync: updateAgreement, isLoading: updateLoading } = useUpdateAgreement()
  const [acceptInfoDialogOpen, setAcceptInfoDialogOpen] = useState<boolean>(false)

  const ownsAgreement = agreement.data && user.data?.matchUserId(agreement.data.userId)
  if (agreement.isLoading || user.isLoading || (agreement.data?.resourceIds && fields.isLoading)) {
    return <Loading />
  }

  const onComplete = (status: AgreementStatus) => {
    if (status === 'agreed') {
      updateAgreement({ agreementId, status, agreedIds: agreement.data?.resourceIds })
        .then(() => {
          // This would be better in the query definition.
          // Since there is no "action items" type endpoint for the user, we need to invalidate
          // control groups, which is in land manager. profile/updateAgreement can
          // not import from landmanager. This is a workaround.
          queryClient.invalidateQueries([CONTROL_GROUPS_QUERY_KEY.CONTROL_GROUPS_SEARCH])
        })
        .catch(() => {
          enqueueSnackbar(t('failedTryAgain', { ns: 'impact/pages/Agreements' }))
        })
    } else {
      updateAgreement({ agreementId, status })
        .then(() => {
          navigate('/admin/grower-management/error-report?t=declined')
          enqueueSnackbar(t('declined'))
        })
        .catch(() => {
          enqueueSnackbar(t('failedTryAgain', { ns: 'impact/pages/Agreements' }))
        })
    }
  }

  const closeAcceptDialog = () => {
    setAcceptInfoDialogOpen(false)
    navigate('../')
  }

  const showActionButtons =
    agreement.data?.status === 'pending' && agreement.data?.isAllowed('can_agree')

  return (
    <Can useAny={[AuthUserPermission.AGREEMENTS_ADMIN_REVIEW_READ]} fallback={<Forbidden />}>
      <Grid container>
        <Grid container spacing={6}>
          <Grid item lg={9} md={8} xs={12}>
            <Stack spacing={2}>
              {agreementCopy && <Markdown overrides={markdownOverrides}>{agreementCopy}</Markdown>}
              {fields.data && fields.data.length > 0 ? (
                <DataGridPro<FieldModel>
                  autoHeight
                  rows={fields.data as FieldModel[]}
                  columns={[
                    fieldThumbnailColumn({ t }),
                    fieldNameLocationColumn({ t }),
                    fieldAcresColumn({ t }),
                  ]}
                />
              ) : (
                <FriendlyError message={t('noFieldsFound')} />
              )}
            </Stack>
          </Grid>
          <Grid item lg={3} md={4} xs={12}>
            <Stack spacing={2}>
              {showActionButtons && (
                <>
                  <Typography
                    variant="h5"
                    color="text.secondary"
                    textAlign={{ xs: 'left', md: 'right' }}
                  >
                    {t('pendingTitle')}
                  </Typography>
                  <Paper elevation={0}>
                    <Stack spacing={2} padding={2}>
                      <Stack spacing={3} textAlign={{ xs: 'left', md: 'right' }}>
                        <Stack direction={{ xs: 'row-reverse', md: 'row' }} spacing={2}>
                          <LoadingButton
                            data-testid="decline-all-button"
                            variant="outlined"
                            color="error"
                            onClick={() => onComplete('declined')}
                            disabled={agreement.data?.status !== 'pending'}
                            loading={updateLoading}
                            endIcon={<ThumbDownAltRounded />}
                            fullWidth
                          >
                            {t('declineButton')}
                          </LoadingButton>
                          <LoadingButton
                            data-testid="accept-button"
                            variant="contained"
                            color="success"
                            onClick={() => onComplete('agreed')}
                            endIcon={<HandshakeRounded />}
                            fullWidth
                            loading={updateLoading}
                          >
                            {t('agreeButton')}
                          </LoadingButton>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Paper>
                </>
              )}
              <Stack
                spacing={1}
                padding={showActionButtons ? 2 : 0}
                alignItems={{ xs: 'flex-start', md: 'flex-end' }}
              >
                <Box>{downloadButton}</Box>
                <AgreementResetButton
                  agreementId={agreementId}
                  ownsAgreement={ownsAgreement}
                  setActionModal={setActionModal}
                />
              </Stack>
              <AgreementNotes agreementId={agreementId} />
            </Stack>
          </Grid>
        </Grid>
      </Grid>
      <ResponsiveDialog open={acceptInfoDialogOpen} onClose={closeAcceptDialog}>
        <DialogTitle>{t('congratulations')}</DialogTitle>
        <DialogContent>
          <Markdown overrides={markdownOverrides}>{agreementCopy}</Markdown>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAcceptDialog}>{t('okay')}</Button>
        </DialogActions>
      </ResponsiveDialog>
    </Can>
  )
}
